/** Admin — Activities CRUD + approval/publish workflow (ownership-aware). */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, CheckCircle2, Send, Archive, Trash2, Store, Pencil } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/app/providers/AuthProvider';
import { listActivities, createActivity, updateActivity, approveActivity, publishActivity, archiveActivity, deleteActivity } from '@/services/api/activities';
import { listPlaces } from '@/services/api/places';
import { formatApiError } from '@/services/api/client';
import { CoverImage } from '@/components/media/Cover';
import { ActivityFormDialog } from '@/pages/admin/forms/ActivityFormDialog';
import { ADMIN_MANAGE } from '@/constants/testIds';

const STATUS_LABELS = {
	approved: 'Approvato', pending: 'In attesa', rejected: 'Rifiutato',
	published: 'Pubblicato', unpublished: 'Non pubblicato', draft: 'Bozza', archived: 'Archiviato',
};
const statusLabel = (s) => STATUS_LABELS[s] || s;

export default function ActivitiesAdmin() {
	const { user, can } = useAuth();
	const qc = useQueryClient();
	const [dialog, setDialog] = useState({ open: false, initial: null });
	const { data = [] } = useQuery({ queryKey: ['activities'], queryFn: listActivities });
	const places = useQuery({ queryKey: ['places'], queryFn: listPlaces, retry: false });
	const inv = () => qc.invalidateQueries({ queryKey: ['activities'] });
	const ok = (msg) => () => { toast.success(msg); inv(); };
	const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
	const createM = useMutation({ mutationFn: createActivity, onSuccess: () => { toast.success('Attività creata'); setDialog({ open: false, initial: null }); inv(); }, onError: err });
	const updateM = useMutation({ mutationFn: ({ id, body }) => updateActivity(id, body), onSuccess: () => { toast.success('Attività aggiornata'); setDialog({ open: false, initial: null }); inv(); }, onError: err });
	const apprM = useMutation({ mutationFn: approveActivity, onSuccess: ok('Approvata'), onError: err });
	const pubM = useMutation({ mutationFn: publishActivity, onSuccess: ok('Pubblicata'), onError: err });
	const arcM = useMutation({ mutationFn: archiveActivity, onSuccess: ok('Archiviata'), onError: err });
	const delM = useMutation({ mutationFn: deleteActivity, onSuccess: ok('Eliminata'), onError: err });

	const onSubmit = (body) => {
		if (dialog.initial) updateM.mutate({ id: dialog.initial.id, body });
		else createM.mutate(body);
	};

	return (
		<div data-testid={ADMIN_MANAGE.activitiesPage} className="mx-auto max-w-5xl">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Attività</h1>
					{!can('manage_content') && <p className="text-sm text-slate-500">Come {user?.role} gestisci solo le tue richieste (in attesa di approvazione).</p>}
				</div>
				{can('create_content') && (
					<Button data-testid={ADMIN_MANAGE.activityCreateBtn} onClick={() => setDialog({ open: true, initial: null })} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300"><Plus size={16} className="mr-1" /> {can('manage_content') ? 'Nuova attività' : 'Richiedi attività'}</Button>
				)}
			</div>

			<ActivityFormDialog
				open={dialog.open}
				onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
				initial={dialog.initial}
				places={places.data ?? []}
				canManage={can('manage_content')}
				onSubmit={onSubmit}
				pending={createM.isPending || updateM.isPending}
			/>

			<div className="mt-6 space-y-2">
				{data.map((a) => (
					<div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3">
						<div className="flex min-w-0 items-center gap-3">
							<CoverImage url={a.cover_url} icon={Store} className="h-12 w-12 shrink-0 rounded-lg" />
							<div><p className="font-semibold">{a.title}</p><p className="text-xs text-slate-500">{a.category} · {a.slug}</p></div>
						</div>
						<div className="flex items-center gap-2">
							<Badge className={a.approval_status === 'approved' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-amber-400/10 text-amber-300'}>{statusLabel(a.approval_status)}</Badge>
							<Badge className={a.publication_status === 'published' ? 'bg-sky-400/10 text-sky-300' : 'bg-slate-700/40 text-slate-400'}>{statusLabel(a.publication_status)}</Badge>
							<Button size="sm" variant="outline" data-testid={ADMIN_MANAGE.activityEditBtn} onClick={() => setDialog({ open: true, initial: a })} className="border-slate-600 text-slate-300"><Pencil size={13} /></Button>
							{can('approve_content') && a.approval_status !== 'approved' && <Button size="sm" variant="outline" onClick={() => apprM.mutate(a.id)} className="border-emerald-400/40 text-emerald-300"><CheckCircle2 size={13} className="mr-1" />Approva</Button>}
							{can('publish_content') && a.approval_status === 'approved' && a.publication_status !== 'published' && <Button size="sm" variant="outline" onClick={() => pubM.mutate(a.id)} className="border-sky-400/40 text-sky-300"><Send size={13} className="mr-1" />Pubblica</Button>}
							{can('manage_content') && a.status !== 'archived' && <Button size="sm" variant="outline" data-testid="activity-archive-button" onClick={() => arcM.mutate(a.id)} className="border-slate-600 text-slate-300"><Archive size={13} /></Button>}
							<Button size="sm" variant="outline" data-testid="activity-delete-button" onClick={() => delM.mutate(a.id)} className="border-rose-400/40 text-rose-300"><Trash2 size={13} /></Button>
						</div>
					</div>
				))}
				{data.length === 0 && <p className="text-slate-500">Nessuna attività.</p>}
			</div>
		</div>
	);
}
